import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AppShell from "./components/layout/AppShell";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Registry from "./pages/Registry";
import MeasureCreate from "./pages/MeasureCreate";
import MeasureDetail from "./pages/MeasureDetail";
import MeasurePrint from "./pages/MeasurePrint";
import Tasks from "./pages/Tasks";
import AuditLogs from "./pages/AuditLogs";
import ComingSoon from "./pages/ComingSoon";
import UsersRoles from "./pages/admin/UsersRoles";
import LguSettings from "./pages/admin/LguSettings";
import { CommitteesPage, DeadlinesPage, DirectoryPage, TemplatesPage, TermsPage } from "./pages/admin/ConfigPages";
import Numbering from "./pages/admin/Numbering";
import WorkflowConfig from "./pages/admin/WorkflowConfig";
import Integrations from "./pages/admin/Integrations";
import PlatformLgus from "./pages/admin/PlatformLgus";
import { CommitteeReports, Hearings, Referrals } from "./pages/committees/CommitteeOps";
import Sessions from "./pages/sessions/Sessions";
import SessionDetail from "./pages/sessions/SessionDetail";
import SessionPrint from "./pages/sessions/SessionPrint";
import CertifiedCopyPrint from "./pages/CertifiedCopyPrint";
import LegislativeCalendar from "./pages/calendar/LegislativeCalendar";
import ArchivePage from "./pages/archive/ArchivePage";
import PublicPortal from "./pages/public/PublicPortal";
import { PortalNotices, PortalReleases, PortalRequests, PortalSettings } from "./pages/portaladmin/PortalAdmin";
import { ExecutiveActions, ExternalReviews, PostApprovalSettings, PublicationMonitor } from "./pages/postapproval/PostApprovalPages";
import CommitteeDetail, { LegislatorProfile } from "./pages/committees/CommitteeDetail";
import ReportView from "./pages/reports/ReportView";
import CustomReports from "./pages/reports/CustomReports";
import { ExportHistory, ReportDefinitions, ScheduledReports } from "./pages/reports/ReportAdmin";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (!user.lgu) return <Navigate to="/platform/lgus" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/public/:code/*" element={<PublicPortal />} />
          <Route path="/measures/:id/print" element={<Protected><MeasurePrint /></Protected>} />
          <Route path="/sessions/:id/print/:kind" element={<Protected><SessionPrint /></Protected>} />
          <Route path="/measures/:id/certified-copy" element={<Protected><CertifiedCopyPrint /></Protected>} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route index element={<Home />} />
            <Route path="registry/:view" element={<Registry />} />
            <Route path="measures/new" element={<MeasureCreate />} />
            <Route path="measures/:id" element={<MeasureDetail />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="audit" element={<AuditLogs />} />
            <Route path="committees" element={<CommitteesPage />} />
            <Route path="committees/:id" element={<CommitteeDetail />} />
            <Route path="directory/legislators/:id" element={<LegislatorProfile />} />
            <Route path="committees/referrals" element={<Referrals />} />
            <Route path="committees/hearings" element={<Hearings />} />
            <Route path="committees/reports" element={<CommitteeReports />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="sessions/:id" element={<SessionDetail />} />
            <Route path="executive-actions" element={<ExecutiveActions />} />
            <Route path="external-review" element={<ExternalReviews />} />
            <Route path="publication" element={<PublicationMonitor />} />
            <Route path="admin/post-approval" element={<PostApprovalSettings />} />
            <Route path="calendar" element={<LegislativeCalendar />} />
            <Route path="archive" element={<ArchivePage />} />
            <Route path="portal/releases" element={<PortalReleases />} />
            <Route path="portal/notices" element={<PortalNotices />} />
            <Route path="portal/requests" element={<PortalRequests />} />
            <Route path="portal/settings" element={<PortalSettings />} />
            <Route path="reports/:key" element={<ReportView />} />
            <Route path="reports-custom" element={<CustomReports />} />
            <Route path="reports-schedules" element={<ScheduledReports />} />
            <Route path="reports-history" element={<ExportHistory />} />
            <Route path="reports/history" element={<ExportHistory />} />
            <Route path="reports-definitions" element={<ReportDefinitions />} />
            <Route path="admin/users" element={<UsersRoles />} />
            <Route path="admin/lgu" element={<LguSettings />} />
            <Route path="admin/terms" element={<TermsPage />} />
            <Route path="admin/directory" element={<DirectoryPage />} />
            <Route path="admin/workflow" element={<WorkflowConfig />} />
            <Route path="admin/numbering" element={<Numbering />} />
            <Route path="admin/templates" element={<TemplatesPage />} />
            <Route path="admin/deadlines" element={<DeadlinesPage />} />
            <Route path="admin/integrations" element={<Integrations />} />
            <Route path="platform/lgus" element={<PlatformLgus />} />
            <Route path="coming/:slug" element={<ComingSoon />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
